import { useEffect } from 'react';
import { Button, useDialog } from '@workspace/design-system/src/components';
import { Channel } from '@src/models/channel';
import { CustomerInfoEditModal } from './customer-info-edit-modal';
import type { CustomerInfoFormData } from './customer-info-edit-modal';
import { useChannelContact } from '@src/hooks/channel/queries';
import { formatPhoneNumberForDisplay } from '@src/utils/phone';

export interface CustomerInfoTabProps {
    channel: Channel;
    onSaveCustomerInfo: (data: CustomerInfoFormData) => Promise<void>;
}

export function CustomerInfoTab({ channel, onSaveCustomerInfo }: CustomerInfoTabProps) {
    const { openDialog, closeDialog } = useDialog();
    const { data: contactData, isLoading } = useChannelContact(channel.channelId);
    
    // API에서 가져온 고객 정보가 있으면 사용, 없으면 props로 받은 channel 사용
    const displayChannel = contactData 
        ? { ...channel, ...contactData }
        : channel;

    // 고객 정보가 있는지 확인
    const hasCustomerInfo = Boolean(
        displayChannel.customerName ||
        displayChannel.businessType ||
        displayChannel.contactName ||
        displayChannel.contactTitle ||
        displayChannel.contactEmail ||
        displayChannel.contactPhone ||
        displayChannel.memo
    );

    const businessTypeLabels: Record<string, string> = {
        none: '해당 없음',
        company: '일반 기업',
        agency: '이벤트∙광고대행사',
        k12: '초∙중∙고등학교',
        university: '대학교∙대학원',
        public: '공공기관∙지자체∙비영리단체',
        library: '도서관∙과학관∙박물관',
    };

    const handleEditClick = ( mode: 'required' | 'edit') => {
        openDialog(
            <CustomerInfoEditModal
                channel={displayChannel}
                mode={mode}
                onlyEdit={true}
                onClose={closeDialog}
                onSave={async (data) => {
                    await onSaveCustomerInfo(data);
                    closeDialog();
                }}
            />
        );
    };

    if (!hasCustomerInfo) {
        // 고객 정보가 없는 경우
        return (
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch flex flex-col justify-start items-end gap-4">
                    <div className="w-full h-60 outline outline-1 outline-[#F0F0F0] flex flex-col justify-center items-center">
                        <div className="text-center justify-center text-[#BFBFBF] text-sm font-normal leading-5">
                            입력된 고객 정보가 없습니다.
                        </div>
                    </div>
                    <div className="inline-flex justify-end items-start gap-3">
                        <Button
                            styleType="outline-primary"
                            styleSize="S-32"
                            onClick={() => handleEditClick('required')}
                        >
                            고객 정보 입력하기
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // 고객 정보가 있는 경우
    return (
        <div className="self-stretch flex flex-col justify-start items-start gap-6">
            <div className="self-stretch flex flex-col justify-start items-end gap-4">
                <div className="self-stretch outline outline-1 outline-[#F0F0F0] inline-flex flex-col justify-start items-start">
                    {/* 고객명 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">고객명</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : (displayChannel.customerName || '-')}
                            </div>
                        </div>
                    </div>

                    {/* 비즈니스 유형 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">비즈니스 유형</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : (displayChannel.businessType ? businessTypeLabels[displayChannel.businessType] || displayChannel.businessType : '-')}
                            </div>
                        </div>
                    </div>

                    {/* 담당자 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">담당자</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : (displayChannel.contactName || '-')}
                            </div>
                        </div>
                    </div>

                    {/* 직책 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">직책</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : (displayChannel.contactTitle || '-')}
                            </div>
                        </div>
                    </div>

                    {/* 이메일 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">이메일</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : (displayChannel.contactEmail || '-')}
                            </div>
                        </div>
                    </div>

                    {/* 연락처 */}
                    <div className="self-stretch h-10 border-b border-[#F0F0F0] inline-flex justify-between items-center">
                        <div data-type="title" className="w-28 h-10 px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4 line-clamp-1">연락처</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 flex justify-start items-center gap-1">
                            <div className="text-center justify-center text-[#101010] text-sm font-normal leading-5">
                                {isLoading ? '로딩 중...' : formatPhoneNumberForDisplay(displayChannel.contactPhone)}
                            </div>
                        </div>
                    </div>

                    {/* 메모 */}
                    <div className="self-stretch inline-flex justify-between items-start">
                        <div data-type="title" className="w-28 min-h-[60px] px-4 py-3 bg-[#F5F5F5] flex justify-start items-start gap-2">
                            <div className="justify-start text-[#101010] text-sm font-bold leading-4">메모</div>
                        </div>
                        <div className="flex-1 self-stretch px-4 py-3 flex justify-start items-start gap-1">
                            <div className="text-[#101010] text-sm font-normal leading-5 whitespace-pre-wrap">
                                {isLoading ? '로딩 중...' : (displayChannel.memo || '-')}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="inline-flex justify-end items-start gap-3">
                    <Button
                        styleType="outline-primary"
                        styleSize="S-32"
                        onClick={() => handleEditClick('edit')}
                    >
                        고객 정보 수정하기
                    </Button>
                </div>
            </div>
        </div>
    );
}

